import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { getParcels } from '../../api/parcels';
import { getCurrentWeather, getForecast } from '../../api/weather';

import { Select } from '../../components/common/Input';
import Card from '../../components/common/Card';
import { FullPageSpinner } from '../../components/common/Spinner';
import Badge from '../../components/common/Badge';

const WeatherIcon = ({ condition = '' }) => {
  const c = condition.toLowerCase();

  if (c.includes('rain')) {
    return <span className="text-4xl">🌧️</span>;
  }

  if (c.includes('cloud')) {
    return <span className="text-4xl">☁️</span>;
  }

  if (c.includes('snow')) {
    return <span className="text-4xl">❄️</span>;
  }

  if (c.includes('storm') || c.includes('thunder')) {
    return <span className="text-4xl">⛈️</span>;
  }

  if (c.includes('fog') || c.includes('mist')) {
    return <span className="text-4xl">🌫️</span>;
  }

  return <span className="text-4xl">☀️</span>;
};

export default function Weather() {
  const [parcels, setParcels] = useState([]);
  const [selectedParcel, setSelectedParcel] = useState('');
  const [current, setCurrent] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weatherLoading, setWeatherLoading] = useState(false);

  useEffect(() => {
    getParcels()
      .then((res) => {
        const list = res.data?.results ?? res.data;

        setParcels(list);

        if (list.length > 0) {
          setSelectedParcel(String(list[0].id));
        }
      })
      .catch(() => {
        toast.error('Неуспешно вчитување на парцелите');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedParcel) return;

    setWeatherLoading(true);

    Promise.all([
      getCurrentWeather(selectedParcel),
      getForecast(selectedParcel),
    ])
      .then(([cRes, fRes]) => {
        const raw = cRes.data?.current ?? cRes.data;

        setCurrent({
          temperature: raw.temp_c,
          description: raw.condition?.text ?? '',
          feels_like: raw.feelslike_c,
          humidity: raw.humidity,
          wind_speed:
            raw.wind_kph != null
              ? (raw.wind_kph / 3.6).toFixed(1)
              : null,
          pressure: raw.pressure_mb,
          visibility:
            raw.vis_km != null
              ? raw.vis_km * 1000
              : null,
          clouds: raw.cloud,
        });

        const days =
          fRes.data?.forecast?.forecastday ?? [];

        const hours = days.flatMap((d) =>
          (d.hour ?? []).map((h) => ({
            dt_txt: h.time,
            temperature: h.temp_c,
            humidity: h.humidity,
            description: h.condition?.text ?? '',
            wind_speed:
              h.wind_kph != null
                ? (h.wind_kph / 3.6).toFixed(1)
                : null,
          }))
        );

        setForecast(hours);
      })
      .catch(() => {
        toast.error('Неуспешно вчитување на временски податоци');

        setCurrent(null);
        setForecast([]);
      })
      .finally(() => {
        setWeatherLoading(false);
      });
  }, [selectedParcel]);

  const handleDownloadWeather = () => {
    if (!current) {
      toast.error('Нема временски податоци за преземање');
      return;
    }

    const selectedParcelData = parcels.find(
      (p) => String(p.id) === String(selectedParcel)
    );

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text('Weather Report', 20, 20);

    doc.setFontSize(12);

    doc.text(
      `Parcel: ${selectedParcelData?.parcel_name || selectedParcel}`,
      20,
      40
    );

    doc.text(
      `Temperature: ${current.temperature ?? '-'} °C`,
      20,
      55
    );

    doc.text(
      `Condition: ${current.description || '-'}`,
      20,
      70
    );

    doc.text(
      `Humidity: ${current.humidity ?? '-'}%`,
      20,
      85
    );

    doc.text(
      `Wind Speed: ${current.wind_speed ?? '-'} m/s`,
      20,
      100
    );

    doc.text(
      `Pressure: ${current.pressure ?? '-'} hPa`,
      20,
      115
    );

    doc.save(
      `weather_${selectedParcelData?.parcel_name || selectedParcel}.pdf`
    );
  };

  if (loading) {
    return <FullPageSpinner />;
  }

  const chartData = forecast
    .slice(0, 10)
    .map((f) => ({
      time: f.dt_txt
        ? format(new Date(f.dt_txt), 'MM/dd HH:mm')
        : '',
      temp: parseFloat(
        f.temperature ?? 0
      ).toFixed(1),
      humidity: parseInt(
        f.humidity ?? 0
      ),
    }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Временска прогноза
        </h1>

        <p className="text-gray-500 text-sm mt-1">
          Тековни услови и прогноза за вашите парцели
        </p>
      </div>

      <div className="flex items-end gap-3 max-w-xl">
        <div className="flex-1">
          <Select
            label="Изберете парцела"
            value={selectedParcel}
            onChange={(e) =>
              setSelectedParcel(e.target.value)
            }
          >
            {parcels.map((p) => {
              return (
                <option
                  key={p.id}
                  value={String(p.id)}
                >
                  {p.parcel_name}
                </option>
              );
            })}
          </Select>
        </div>

        <button
          type="button"
          onClick={handleDownloadWeather}
          disabled={!current || weatherLoading}
          className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Преземи PDF
        </button>
      </div>

      {weatherLoading ? (
        <FullPageSpinner />
      ) : current ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="flex items-center gap-4">
              <WeatherIcon
                condition={
                  current.description ||
                  current.condition
                }
              />

              <div>
                <p className="text-3xl font-bold text-gray-800">
                  {current.temperature}°C
                </p>

                <p className="text-gray-500 text-sm capitalize">
                  {current.description ||
                    current.condition}
                </p>
              </div>
            </Card>

            {[
              {
                label: 'Се чувствува како',
                value: `${current.feels_like ?? '—'}°C`,
              },
              {
                label: 'Влажност',
                value: `${current.humidity ?? '—'}%`,
              },
              {
                label: 'Брзина на ветер',
                value: `${current.wind_speed ?? '—'} м/с`,
              },
            ].map(({ label, value }) => (
              <Card key={label}>
                <p className="text-sm text-gray-500">
                  {label}
                </p>

                <p className="text-xl font-bold text-gray-800 mt-1">
                  {value}
                </p>
              </Card>
            ))}
          </div>

          {forecast.length > 0 && (
            <Card>
              <h2 className="font-semibold text-gray-700 mb-4">
                Прогноза за температура и влажност
              </h2>

              <ResponsiveContainer
                width="100%"
                height={260}
              >
                <BarChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 10,
                    left: -10,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f0f0f0"
                  />

                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 10 }}
                  />

                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 10 }}
                  />

                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10 }}
                  />

                  <Tooltip />

                  <Bar
                    yAxisId="left"
                    dataKey="temp"
                    name="Темп °C"
                    fill="#16a34a"
                    radius={[3, 3, 0, 0]}
                  />

                  <Bar
                    yAxisId="right"
                    dataKey="humidity"
                    name="Влажност %"
                    fill="#93c5fd"
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {forecast.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-700">
                  Детална прогноза
                </h2>
              </div>

              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {[
                      'Датум / Час',
                      'Состојба',
                      'Темп °C',
                      'Влажност',
                      'Ветер',
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left px-5 py-3 text-gray-500 font-medium"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {forecast
                    .slice(0, 10)
                    .map((f, i) => (
                      <tr
                        key={i}
                        className="border-b border-gray-50 hover:bg-gray-50"
                      >
                        <td className="px-5 py-3 text-xs text-gray-500">
                          {f.dt_txt
                            ? format(new Date(f.dt_txt), 'MMM dd, HH:mm') : '—'}
                        </td>

                        <td className="px-5 py-3">
                          <Badge color="blue">
                            {f.description || '—'}
                          </Badge>
                        </td>

                        <td className="px-5 py-3">
                          {f.temperature ?? '—'}
                        </td>

                        <td className="px-5 py-3">
                          {f.humidity ?? '—'}%
                        </td>

                        <td className="px-5 py-3">
                          {f.wind_speed ?? '—'} м/с
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <Card>
          <p className="text-center text-gray-400 py-8 text-sm">
            Нема достапни временски податоци за оваа парцела.
          </p>
        </Card>
      )}
    </div>
  );
}
